import React, { useState } from 'react';

const ManifestModal = ({ 
    isOpen, 
    onClose, 
    onGenerate, 
    loading = false,
    dataset = null 
}) => {
    const [projectName, setProjectName] = useState('');
    const [includeUnlabeled, setIncludeUnlabeled] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = () => {
        if (!projectName.trim()) {
            setError('Project name is required');
            return;
        }
        setError('');
        onGenerate(projectName.trim(), includeUnlabeled);
    };

    const handleClose = () => {
        setProjectName('');
        setIncludeUnlabeled(false);
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Generate Manifest</h2>
                
                {dataset && (
                    <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10">
                        <div className="text-sm text-white/70 mb-1">Dataset</div>
                        <div className="text-white font-medium">{dataset.name}</div>
                        <div className="text-xs text-white/60 mt-1">
                            {dataset.images_count} images • {dataset.annotations_count} annotations
                        </div>
                    </div>
                )}

                <div className="mb-4">
                    <label className="block text-sm font-medium text-white/80 mb-2">
                        Project Name *
                    </label>
                    <input
                        type="text"
                        value={projectName}
                        onChange={(e) => {
                            setProjectName(e.target.value);
                            setError('');
                        }}
                        placeholder="Enter project name for manifest..."
                        className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !loading) {
                                handleSubmit();
                            }
                        }}
                        disabled={loading}
                    />
                    <div className="text-xs text-white/60 mt-1">
                        This will be used as the base name for the manifest file
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-white/80 mb-2">
                        Include Unlabeled Images
                    </label>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setIncludeUnlabeled(false)}
                            className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                                !includeUnlabeled
                                    ? 'bg-white/20 border-white/30 text-white'
                                    : 'bg-white/10 border-white/20 text-white/80 hover:bg-white/15'
                            }`}
                            disabled={loading}
                        >
                            False
                        </button>
                        <button
                            type="button"
                            onClick={() => setIncludeUnlabeled(true)}
                            className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                                includeUnlabeled
                                    ? 'bg-white/20 border-white/30 text-white'
                                    : 'bg-white/10 border-white/20 text-white/80 hover:bg-white/15'
                            }`}
                            disabled={loading}
                        >
                            True
                        </button>
                    </div>
                    <div className="text-xs text-white/60 mt-1">
                        Choose whether to include images without annotations in the manifest
                    </div>
                </div>

                {error && (
                    <div className="mb-4 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded p-2">
                        {error}
                    </div>
                )}

                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading || !projectName.trim()}
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Generating...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Generate Manifest
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ManifestModal;
